<template>
  <div class="ledger-page pb-5 pb-md-2">
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner variant="primary" />
    </div>
    <b-card
      v-else
      class="rounded-lg ledger-dense-form"
    >
      <h4 class="mb-50">
        {{ isEdit ? $t('ledger_sup_edit') : $t('ledger_sup_new') }}
      </h4>
      <p class="text-muted small mb-50">
        {{ $t('ledger_sup_no_journal') }}
        {{ $t('ledger_sup_account_hint') }}
      </p>
      <div
        v-if="isEdit"
        class="mb-1"
      >
        <small class="text-muted d-block">{{ $t('ledger_gl_account') }}</small>
        <strong>{{ glText }}</strong>
      </div>
      <b-row>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_code')">
            <b-form-input
              v-model="form.code"
              maxlength="32"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_name')">
            <b-form-input v-model="form.name" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_phone')">
            <b-form-input v-model="form.phone" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_address')">
            <b-form-input v-model="form.address" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_credit_limit')">
            <b-form-input
              v-model.number="form.credit_limit"
              type="number"
              min="0"
              step="0.01"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_account_code')">
            <b-form-input v-model="form.account_code" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_tax_number')">
            <b-form-input v-model="form.tax_number" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_nationality')">
            <b-form-input v-model="form.nationality" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_email')">
            <b-form-input v-model="form.email" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_contact_name')">
            <b-form-input v-model="form.contact_name" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_city')">
            <b-form-input v-model="form.city" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_region')">
            <b-form-input v-model="form.region" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_country')">
            <b-form-input v-model="form.country" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_postal_code')">
            <b-form-input v-model="form.postal_code" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_po_box')">
            <b-form-input v-model="form.po_box" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_fax')">
            <b-form-input v-model="form.fax" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_representative')">
            <b-form-input v-model="form.representative" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_bank_account')">
            <b-form-input v-model="form.bank_account" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_sup_notes')">
            <b-form-input v-model="form.notes" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
          class="d-flex align-items-end"
        >
          <b-form-checkbox
            v-model="form.in_use"
            class="mb-50"
          >
            {{ $t('ledger_sup_in_use') }}
          </b-form-checkbox>
        </b-col>
        <template v-if="isEdit">
          <b-col
          cols="6"
          lg="3"
          >
            <b-form-group :label="$t('ledger_sup_debit')">
              <b-form-input
                :value="money(supplier && supplier.debit)"
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col
          cols="6"
          lg="3"
          >
            <b-form-group :label="$t('ledger_sup_credit')">
              <b-form-input
                :value="money(supplier && supplier.credit)"
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col
          cols="6"
          lg="3"
          >
            <b-form-group :label="$t('ledger_sup_entered_by')">
              <b-form-input
                :value="personName(supplier && supplier.entered_by)"
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col
          cols="6"
          lg="3"
          >
            <b-form-group :label="$t('ledger_sup_entered_at')">
              <b-form-input
                :value="dateText(supplier && supplier.entered_at)"
                disabled
              />
            </b-form-group>
          </b-col>
        </template>
      </b-row>
      <b-button
        variant="primary"
        class="rounded-pill"
        :disabled="saving || !canSave"
        @click="save"
      >
        {{ $t('ledger_assets_save') }}
      </b-button>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BFormCheckbox, BButton, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { createSupplier, getSupplier, updateSupplier } from '@/api/accounting/suppliers'
import {
  oneFrom, formatLedgerMoney, roundMoney, apiErrorMessage, glAccountText,
} from '@/utils/ledger'

const TEXT_KEYS = [
  'address', 'phone', 'account_code', 'notes', 'tax_number', 'nationality', 'email',
  'contact_name', 'po_box', 'city', 'postal_code', 'country', 'region', 'fax',
  'representative', 'bank_account',
]

function emptyToNull(value) {
  const text = String(value || '').trim()
  return text || null
}

function moneyOrNull(value) {
  if (value === null || value === undefined || value === '') return null
  return roundMoney(value)
}

function asText(value) {
  if (value === null || value === undefined) return ''
  return String(value)
}

function blankForm() {
  return {
    code: '',
    name: '',
    address: '',
    phone: '',
    credit_limit: null,
    account_code: '',
    notes: '',
    tax_number: '',
    nationality: '',
    email: '',
    contact_name: '',
    po_box: '',
    city: '',
    postal_code: '',
    country: '',
    region: '',
    fax: '',
    representative: '',
    bank_account: '',
    in_use: true,
  }
}

export default {
  name: 'SupplierForm',
  components: {
    BCard, BRow, BCol, BFormGroup, BFormInput, BFormCheckbox, BButton, BSpinner,
  },
  data() {
    return {
      loading: false,
      saving: false,
      supplier: null,
      baseline: null,
      form: blankForm(),
    }
  },
  computed: {
    isEdit() {
      return Boolean(this.$route.params.id)
    },
    glText() {
      return glAccountText(this.supplier) || '—'
    },
    canSave() {
      const code = String(this.form.code || '').trim()
      if (!code || code.length > 32) return false
      if (this.isEdit && !Object.keys(this.changedPayload()).length) return false
      return true
    },
  },
  mounted() {
    if (this.isEdit) this.load()
  },
  methods: {
    money(value) {
      if (value === null || value === undefined || value === '') return '—'
      return formatLedgerMoney(value, this.$i18n.locale)
    },
    personName(value) {
      if (!value) return '—'
      if (typeof value === 'string' || typeof value === 'number') return String(value)
      return value.name || value.name_ar || value.full_name || value.email || (value.id ? `#${value.id}` : '—')
    },
    dateText(value) {
      if (!value) return '—'
      return String(value).replace('T', ' ').replace(/\.\d+Z?$/, '').slice(0, 19)
    },
    normalized() {
      const body = {
        code: String(this.form.code || '').trim(),
        name: String(this.form.name || '').trim(),
        credit_limit: moneyOrNull(this.form.credit_limit),
        in_use: Boolean(this.form.in_use),
      }
      TEXT_KEYS.forEach(key => {
        body[key] = emptyToNull(this.form[key])
      })
      return body
    },
    changedPayload() {
      const next = this.normalized()
      const prev = this.baseline || {}
      const payload = {}
      Object.keys(next).forEach(key => {
        if (next[key] !== prev[key]) payload[key] = next[key]
      })
      return payload
    },
    createPayload() {
      const next = this.normalized()
      const payload = {
        code: next.code,
        name: next.name,
        in_use: next.in_use,
      }
      if (next.credit_limit !== null) payload.credit_limit = next.credit_limit
      TEXT_KEYS.forEach(key => {
        if (next[key]) payload[key] = next[key]
      })
      return payload
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
    async load() {
      this.loading = true
      try {
        const supplier = oneFrom(await getSupplier(this.$route.params.id))
        this.supplier = supplier
        const form = blankForm()
        form.code = asText(supplier.code)
        form.name = asText(supplier.name)
        form.in_use = Boolean(supplier.in_use)
        form.credit_limit = moneyOrNull(supplier.credit_limit)
        TEXT_KEYS.forEach(key => {
          form[key] = asText(supplier[key])
        })
        this.form = form
        this.baseline = this.normalized()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.loading = false
      }
    },
    async save() {
      if (!this.canSave || this.saving) return
      this.saving = true
      try {
        const res = this.isEdit
          ? await updateSupplier(this.$route.params.id, this.changedPayload())
          : await createSupplier(this.createPayload())
        const supplier = oneFrom(res)
        this.toast(this.$t('ledger_sup_saved'), 'success')
        const id = (supplier && supplier.id) || this.$route.params.id
        if (id) this.$router.push({ name: 'ledger-suppliers-view', params: { id } })
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss">
.ledger-dense-form .card-body { padding: 0.75rem 1rem; }
.ledger-dense-form .form-group { margin-bottom: 0.35rem; }
.ledger-dense-form .form-group > label {
  font-size: 0.75rem;
  margin-bottom: 0.1rem;
}
.ledger-dense-form .form-control,
.ledger-dense-form .custom-select {
  height: 1.85rem;
  min-height: 1.85rem;
  padding: 0.15rem 0.45rem;
  font-size: 0.8125rem;
}
</style>
